/**
 * Club-related constants
 * Centralized definitions for club types and skill levels
 */

import { Users, Zap, Dumbbell } from 'lucide-react';

export const CLUB_TYPES = [
  { value: 'Social', label: 'Social', icon: Users, description: 'Casual, community-focused runs' },
  { value: 'Training-Focused', label: 'Training-Focused', icon: Zap, description: 'Competitive training and racing' },
  { value: 'Hyrox', label: 'Hyrox', icon: Dumbbell, description: 'Hyrox training and racing' }
];

export const SKILL_LEVELS = [
  { value: 'All levels', label: 'All levels', description: 'Open to runners of all skill levels' },
  { value: 'Beginners', label: 'Beginners', description: 'New to running or getting back into it' },
  { value: 'Intermediate', label: 'Intermediate', description: 'Some running experience, regular training' },
  { value: 'Advanced', label: 'Advanced', description: 'Experienced runners, competitive training' },
  { value: 'Mixed pace groups', label: 'Mixed pace groups', description: 'Multiple pace groups for different skill levels' }
];

/** 1 mi = 1.609344 km; pace in min/km = min/mi ÷ this factor. */
const KM_PER_MILE = 1.609344;

function parseMinSecToken(token) {
  const m = String(token).trim().match(/^(\d+):(\d{2})$/);
  if (!m) return NaN;
  return parseInt(m[1], 10) + parseInt(m[2], 10) / 60;
}

function formatMinSec(minutesFloat) {
  if (!Number.isFinite(minutesFloat) || minutesFloat < 0) return "0:00";
  const totalSec = Math.round(minutesFloat * 60);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

function milePaceMinutesToKmPaceMinutes(mileMinutes) {
  return mileMinutes / KM_PER_MILE;
}

/**
 * Convert a mile pace range label (what the API stores) to an equivalent min/km label for admin UI.
 * Unknown shapes are returned unchanged.
 */
export function paceLabelMileToKm(mileLabel) {
  const s = String(mileLabel).replace(/\u2013/g, "-").trim();
  const lt = s.match(/^<\s*(\d+:\d{2})\s*$/);
  if (lt) {
    const km = milePaceMinutesToKmPaceMinutes(parseMinSecToken(lt[1]));
    return `< ${formatMinSec(km)}`;
  }
  const plus = s.match(/^(\d+:\d{2})\s*\+$/);
  if (plus) {
    const km = milePaceMinutesToKmPaceMinutes(parseMinSecToken(plus[1]));
    return `${formatMinSec(km)}+`;
  }
  const range = s.match(/^(\d+:\d{2})\s*-\s*(\d+:\d{2})$/);
  if (range) {
    const a = milePaceMinutesToKmPaceMinutes(parseMinSecToken(range[1]));
    const b = milePaceMinutesToKmPaceMinutes(parseMinSecToken(range[2]));
    return `${formatMinSec(a)}-${formatMinSec(b)}`;
  }
  return mileLabel;
}

/** Console pace pill label: still sends mile `label` to the API when unit is km. */
export function getPaceRangeDisplayLabel(paceRange, unit) {
  if (unit === "km") return paceLabelMileToKm(paceRange.label);
  return paceRange.label;
}

/** Pace range options for run create/edit (value = code, label = display; backend expects labels). */
export const PACE_RANGES = [
  { value: 'lt_6', label: '< 6:00' },
  { value: '6_659', label: '6:00-6:59' },
  { value: '7_759', label: '7:00-7:59' },
  { value: '8_859', label: '8:00-8:59' },
  { value: '9_959', label: '9:00-9:59' },
  { value: '10_1059', label: '10:00-10:59' },
  { value: '11_1159', label: '11:00-11:59' },
  { value: '12_plus', label: '12:00+' },
];

// Validation arrays (extracted from options for consistency)
export const VALID_CLUB_TYPES = CLUB_TYPES.map(type => type.value);
export const VALID_SKILL_LEVELS = SKILL_LEVELS.map(level => level.value);
export const VALID_PACE_RANGES = PACE_RANGES.map(range => range.value);
